import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
} from "react";

/**
 * The console's one layout breakpoint, shared with styles.css (search for
 * 860px). At or under it the rail becomes a tab bar and tables become lists.
 * Where only CSS changes, CSS owns the switch; this is for the places where
 * the phone layout is different markup, not the same markup restyled.
 */
export const COMPACT_QUERY = "(max-width: 860px)";

/**
 * Whether a media query matches, kept current as the window changes (a phone
 * rotating, a desktop window dragged narrow). False where there is no
 * `matchMedia` at all, which is jsdom: the tests render the desktop layout.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (notify: () => void) => {
      if (typeof window.matchMedia !== "function") return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", notify);
      return () => list.removeEventListener("change", notify);
    },
    [query],
  );
  return useSyncExternalStore(subscribe, () => matches(query), () => false);
}

/** `useMediaQuery` for a one-off read outside render. */
export function matches(query: string): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(query).matches;
}

export function useCompact(): boolean {
  return useMediaQuery(COMPACT_QUERY);
}

/** Delays `value` by `ms`, so a keystroke does not become a request. */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export interface AsyncState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
}

export interface AsyncResult<T> extends AsyncState<T> {
  /** Re-runs `load` with the current deps. Stable across renders. */
  reload: () => void;
}

/**
 * Runs `load` on mount, whenever `deps` change, and whenever `reload` is
 * called, discarding the result of a superseded call so a slow first response
 * cannot overwrite a fast second one. A reload cancels the call it supersedes
 * on exactly the same terms as a deps change.
 */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    error: null,
    loading: true,
  });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    load()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ data: null, error, loading: false });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  return { ...state, reload };
}

/**
 * Closes a popover on an outside pointer press or on Escape, and hands back
 * the ref to put on the popover's wrapper.
 *
 * Pointer-down on the document rather than a blur handler on the trigger:
 * blur fires BEFORE the click lands on an option, so a blur-based close eats
 * the very selection it exists to allow.
 */
export function useDismiss(
  open: boolean,
  onClose: () => void,
): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const el = ref.current;
      if (el && !el.contains(event.target as Node)) onClose();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  return ref;
}
